// Shared formatting/image helpers for the Influencer Explorer. Trimmed copy
// of the handful of UGC Explorer's utils.js helpers this section actually
// needs, kept local so this section doesn't reach back into UGC Explorer's
// folder for shared plumbing.

// TikTok's signed profile URLs are served as HEIC (TOP_INFLUENCER_FILTERS.md
// §7) - no browser other than Safari decodes HEIC in an <img>, so left alone
// these just render as a broken image. `.heic`/`.heif` can sit right before
// the querystring of a signed URL (`....heic?x-tos-...`), so this checks the
// path rather than the URL's tail.
const HEIC_EXTENSION = /\.heic(?:[?#]|$)|\.heif(?:[?#]|$)/i
export const isHeicUrl = (url) => {
  if (!url) return false
  return HEIC_EXTENSION.test(url)
}

// Instagram's CDN hotlink-protects its thumbnail/profile images - it 403s a
// browser <img> request outright. images.weserv.nl (wsrv.nl) is a
// well-known, no-signup mirror/resize proxy - it fetches the image
// server-side and re-serves it from its own origin, so the request that
// actually reaches Instagram's CDN never comes from this site's browser.
// The same proxy also decodes HEIC server-side and re-encodes as JPEG
// (`&output=jpg`), which is the only way a HEIC source ever reaches the
// browser as something it can actually display.
export const proxiedImageUrl = (url) => {
  if (!url) return null
  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&output=jpg`
}

// Instagram's own CDN (`*.cdninstagram.com` / `*.fbcdn.net`) hotlink-protects
// images, and TikTok's signed URLs serve HEIC - both need the wsrv.nl proxy
// above. This app's own S3-hosted mirrors load directly and don't need the
// proxy at all, so callers check this per-URL.
export const needsImageProxy = (url) => {
  if (!url) return false
  return /cdninstagram\.com|fbcdn\.net/i.test(url) || isHeicUrl(url)
}

// YouTube-style compact view counts - 1,950,384 -> "1.9M", 762,344 -> "762.3K".
// Truncated (not rounded) to one decimal, same as YouTube's own display.
export const formatViews = (views) => {
  if (views === null || views === undefined) return "—"
  const num = Number(views)
  if (Number.isNaN(num)) return "—"
  if (num < 1000) return String(num)

  const units = [
    { value: 1e9, suffix: "B" },
    { value: 1e6, suffix: "M" },
    { value: 1e3, suffix: "K" },
  ]

  const unit = units.find(u => num >= u.value)
  const truncated = Math.floor((num / unit.value) * 10) / 10
  const formatted = Number.isInteger(truncated) ? String(truncated) : truncated.toFixed(1)
  return `${formatted}${unit.suffix}`
}

// Every timestamp the influencer APIs return (`first_seen_at`,
// `last_refreshed_at`, `published_at`, `favourited_at`, the missing-favourite
// entries' `at`) is a proper ISO string with its trailing "Z" already on it
// (TOP_INFLUENCER_FILTERS.md §5, INFLUENCER_PROFILE_API.md §3) - unlike UGC
// Explorer's `stats_history[].at`, this needs no local-time-vs-UTC patching
// before `new Date(...)` parses it.
export const formatUtcDate = (iso) => {
  if (!iso) return null
  try {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  } catch {
    return null
  }
}

// "3 days ago" style label for `published_at` / `favourited_at` - same
// bucketing as UGC Explorer's own formatRelativeTime, trimmed to this
// section's needs.
export const formatRelativeTime = (iso) => {
  if (!iso) return null
  let date
  try {
    date = new Date(iso)
  } catch {
    return null
  }
  if (Number.isNaN(date.getTime())) return null

  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) return formatUtcDate(iso)

  const minute = 60 * 1000, hour = 60 * minute, day = 24 * hour, month = 30 * day, year = 365 * day
  const bucket = (unit, label) => {
    const n = Math.floor(diffMs / unit)
    return n >= 1 ? `${n} ${label}${n === 1 ? "" : "s"} ago` : null
  }

  if (diffMs < minute) return "Just now"
  if (diffMs < hour) return bucket(minute, "minute")
  if (diffMs < day) return bucket(hour, "hour")
  if (diffMs < month) return bucket(day, "day")
  if (diffMs < year) return bucket(month, "month")
  return bucket(year, "year")
}

// A post's `duration_seconds` (INFLUENCER_PROFILE_API.md §3) has no
// ready-made "1:23" string the way a platform's own API sometimes provides -
// same trimmed helper as UGC Explorer's own formatDuration.
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return null
  const total = Math.round(Number(seconds))
  if (!Number.isFinite(total) || total < 0) return null
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

// Best-effort outbound link for the profile detail screen's "not discovered
// yet" state, where there's no stored `profile_url` to render (discovery
// has never seen the account, so nothing was ever fetched or stored) - just
// the route's own `platform`/`username`. Good enough for TikTok/Instagram's
// handle-based URLs; YouTube's real identity is often a channel id rather
// than this `@handle` guess, but it's still a reasonable "check it
// yourself" link, not treated as authoritative anywhere else.
export const buildFallbackProfileUrl = (platform, username) => {
  if (!platform || !username) return null
  const handle = username.replace(/^@/, "")
  if (platform === "tiktok") return `https://www.tiktok.com/@${handle}`
  if (platform === "instagram") return `https://www.instagram.com/${handle}`
  if (platform === "youtube") return `https://www.youtube.com/@${handle}`
  return null
}

// Generic active-filter counter used for the "Filters (N)" toolbar button -
// arrays count their length, booleans count 1 when true, strings count 1
// when non-blank.
export const countActiveFilters = (filters) => {
  let count = 0
  Object.values(filters || {}).forEach((value) => {
    if (Array.isArray(value)) count += value.length
    else if (typeof value === "boolean") { if (value) count += 1 }
    else if (typeof value === "string") { if (value.trim()) count += 1 }
  })
  return count
}
